import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Upload, Lock, Users2, CheckCircle, Clock, Paperclip, X, Loader2, ShieldCheck } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const isImageFile = (s) => /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(s || '');

import { HomeworkAttachment as Attachment } from './HomeworkAttachment';

export const SectionHomework = ({ folderId, folderName, isAdmin }) => {
  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // admin lead assignment
  const [contributors, setContributors] = useState([]);
  const [leadId, setLeadId] = useState('');

  const [handin, setHandin] = useState(null);

  const load = useCallback(async () => {
    try {
      const m = await axios.get(`${API}/sections/${folderId}/homework/mine`);
      setMine(m.data.submissions || []);
    } catch (e) { /* silent */ }
    try {
      const h = await axios.get(`${API}/sections/${folderId}/handin-state`);
      setHandin(h.data);
    } catch (e) { /* silent */ }
  }, [folderId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/contributors/list`).then(r => setContributors(r.data || [])).catch(() => {});
    axios.get(`${API}/sections/${folderId}/leads`).then(r => setLeadId(r.data.lead?.id || '')).catch(() => {});
  }, [isAdmin, folderId]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await axios.post(`${API}/sections/${folderId}/homework/upload`, fd);
      setFileUrl(r.data.file_url);
      setFileName(r.data.filename);
      toast.success('Attachment added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const clearFile = () => { setFileUrl(null); setFileName(null); };

  const submit = async () => {
    if (!content.trim() && !fileUrl) { toast.error('Write your homework or attach a file'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sections/${folderId}/homework`, {
        content, file_url: fileUrl, file_name: fileName, visibility: isPublic ? 'public' : 'private',
      });
      toast.success(isPublic ? 'Homework shared with the group!' : 'Homework submitted privately');
      setContent('');
      clearFile();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const saveLead = async (value) => {
    setLeadId(value);
    try {
      await axios.put(`${API}/sections/${folderId}/leads`, {
        lead_practitioner_id: value || null, co_lead_ids: [],
      });
      toast.success('Section lead updated');
    } catch (e) { toast.error('Failed to update lead'); }
  };

  // Once every item in the section has its own published worksheet, each one carries its own
  // hand-in (or is deliberately no hand-in), so this catch-all box is a second confusing route.
  // It goes as soon as that is true — his expectation, now actually wired.
  const covered = !!handin?.all_covered;
  if (covered && !isAdmin && mine.length === 0) return null;

  return (
    <div className="pt-4 border-t space-y-4" data-testid={`section-homework-${folderId}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-purple-700">
        <Upload className="w-4 h-4" /> Homework
      </div>

      {covered && (
        <p className="text-xs text-gray-500" data-testid={`homework-covered-note-${folderId}`}>
          Every item in this section has its own worksheet now, so there is nothing to upload
          here — you hand in from the worksheet itself.
          {isAdmin && ' Members no longer see this box.'}
        </p>
      )}

      {isAdmin && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
          <Label className="text-xs text-cyan-800">Section Lead (practitioner who reviews this section's homework)</Label>
          <select
            value={leadId}
            onChange={(e) => saveLead(e.target.value)}
            data-testid={`lead-select-${folderId}`}
            className="mt-1 w-full text-sm border rounded-md p-2 bg-white"
          >
            <option value="">Unassigned (shared pool)</option>
            {contributors.map((c) => (
              <option key={c.user_id || c.id} value={c.user_id || c.id}>{c.user?.full_name || c.user?.username || c.full_name || c.username}</option>
            ))}
          </select>
        </div>
      )}

      {/* Submit — hidden once every item has its own worksheet */}
      <div className={`space-y-2 ${covered ? 'hidden' : ''}`}>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`Share your homework for "${folderName}"...`}
          className="min-h-[80px]"
          data-testid={`homework-input-${folderId}`}
        />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid={`homework-visibility-${folderId}`} />
            <span className="text-xs text-gray-600 flex items-center gap-1">
              {isPublic ? <><Users2 className="w-3.5 h-3.5" /> Share with the group so others can learn</> : <><Lock className="w-3.5 h-3.5" /> Submit privately — only my practitioner sees it</>}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.odt"
              className="hidden"
              onChange={handleFile}
              data-testid={`homework-file-input-${folderId}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid={`homework-attach-${folderId}`}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              <span className="ml-1">Attach photo/doc</span>
            </Button>
            <Button onClick={submit} disabled={submitting || uploading} size="sm" data-testid={`homework-submit-${folderId}`} className="bg-gradient-to-r from-cyan-500 to-purple-500">
              {submitting ? 'Submitting…' : 'Submit Homework'}
            </Button>
          </div>
        </div>
        {isPublic && (
          <div
            className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-2.5 py-2"
            data-testid={`homework-anonymity-notice-${folderId}`}
          >
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              Shared work is posted <strong>anonymously</strong> — other members never see your name or
              photo. Please don&apos;t write your name in the text, and don&apos;t attach files or photos
              that show who you are. Your practitioner and the admins still see your name so they can
              give you feedback.
            </span>
          </div>
        )}
        {fileUrl && (
          <div className="flex items-center gap-2 text-xs bg-cyan-50 border border-cyan-200 rounded-md px-2 py-1.5" data-testid={`homework-attached-${folderId}`}>
            <Paperclip className="w-3.5 h-3.5 text-cyan-600" />
            <span className="truncate flex-1 text-cyan-800">{fileName}</span>
            <button onClick={clearFile} className="text-cyan-600 hover:text-cyan-900" data-testid={`homework-attach-remove-${folderId}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* My submissions */}
      {mine.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600">My Submissions</p>
          {mine.map((s) => (
            <div key={s.id} className="text-sm bg-gray-50 rounded-lg p-3" data-testid={`my-submission-${s.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs flex items-center gap-1 text-gray-500">
                  {s.visibility === 'public' ? <Users2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {s.status === 'reviewed' ? <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Reviewed</span> : <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending review</span>}
                </span>
              </div>
              <p className="text-gray-700 mt-1 whitespace-pre-wrap">{s.content}</p>
              <Attachment url={s.file_url} name={s.file_name} />
              {s.feedback && (
                <div className="mt-2 bg-purple-50 border-l-2 border-purple-400 pl-2 py-1 text-purple-800 text-xs">
                  <span className="font-medium">{s.reviewed_by_name}:</span> {s.feedback}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shared work now sits beside each exercise, not in a pile at the end of the section */}
    </div>
  );
};

export default SectionHomework;
